import React, { useState, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';
import Heading from 'components/partials/heading/heading';

import Icons from 'assets/icons';

import { unmountModal } from 'helpers/modal-render';

const MODAL_WRAPPER_ATTR = 'data-modal-wrapper';

interface ModalProps {
  children?: React.ReactNode;
  onCloseCallback?: () => void;
  onConfirmCallback?: () => void | Promise<void>;
  headerText?: string;
  headerSubtext?: string;
  onConfirmButtonLabel?: string;
  disabled?: boolean;
  text?: string;
  onCloseModal?: () => void;
  disableBackdropClick?: boolean;
  formId?: string;
  modalClassName?: string;
  containerClassName?: string;
  footer?: React.ReactNode;
  stepIndicator?: React.ReactNode;
  loading?: boolean;
  showCloseButton?: boolean;
  skipUnmountOnClose?: boolean;
  disableEnterConfirm?: boolean;
  'data-testid'?: string;
}

const Modal = ({
  children,
  onCloseCallback,
  onConfirmCallback,
  headerText,
  headerSubtext,
  onConfirmButtonLabel = 'OK',
  disabled,
  text,
  onCloseModal = () => {},
  disableBackdropClick = false,
  formId,
  modalClassName,
  containerClassName = '',
  footer,
  stepIndicator,
  loading,
  showCloseButton = false,
  skipUnmountOnClose = false,
  disableEnterConfirm = false,
  'data-testid': dataTestId = 'modal',
}: ModalProps): React.JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onClose = useCallback(() => {
    if (!skipUnmountOnClose) {
      unmountModal();
    }
    if (onCloseCallback) {
      onCloseCallback();
    }
  }, [onCloseCallback, skipUnmountOnClose]);

  useEffect(() => {
    const currentUrl = window.location.pathname + window.location.search;

    window.history.pushState(null, '', currentUrl);
    window.addEventListener('popstate', onClose);

    return () => {
      window.removeEventListener('popstate', onClose);
    };
  }, [onClose]);

  const onConfirm = async () => {
    if (!onConfirmCallback) {
      onClose();

      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirmCallback();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalWrapperRf = useRef<HTMLDivElement>(null);
  const modalInnerRf = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: MouseEvent): void => {
    if (disableBackdropClick) return;
    const target = e.target as Node;

    if (!modalInnerRf.current?.contains(target)) {
      const clickedModalWrapper = (target as HTMLElement).closest?.(
        `[${MODAL_WRAPPER_ATTR}]`
      );

      if (
        clickedModalWrapper &&
        clickedModalWrapper !== modalWrapperRf.current
      ) {
        return;
      }

      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      onClose();
      if (skipUnmountOnClose) {
        e.stopPropagation();
        e.preventDefault();
      }
    } else if (e.key === 'Enter' && !formId && !disableEnterConfirm) {
      onConfirm();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown, skipUnmountOnClose);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener(
        'keydown',
        handleKeyDown,
        skipUnmountOnClose
      );
    };
  }, [skipUnmountOnClose, disableBackdropClick, disableEnterConfirm]);

  useEffect(() => {
    if (modalWrapperRf.current) {
      modalWrapperRf.current.focus();
    }
  }, []);

  return (
    <div
      className={classNames(
        'fixed inset-0 z-[701] h-[100vh] max-h-[100vh] overflow-hidden',
        containerClassName
      )}
      ref={modalWrapperRf}
      tabIndex={-1}
      data-testid="modal-wrapper"
      {...{ [MODAL_WRAPPER_ATTR]: true }}
    >
      <div className="absolute inset-0 opacity-60 bg-navy-950" />
      <div className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
        <div
          ref={modalInnerRf}
          className={classNames(
            'mobile:w-[330px] tablet:w-[500px] bg-white p-8 rounded-md overflow-hidden flex flex-col justify-between relative',
            modalClassName
          )}
          data-testid={dataTestId}
        >
          {showCloseButton && (
            <IconButton
              variant="reserve-text"
              size="xs"
              icon={<Icons.Close />}
              onClick={onClose}
              aria-label="Close modal"
              className="absolute right-3 top-3"
              data-testid="close-modal-button"
            />
          )}
          <div className="flex items-start justify-between">
            <Heading
              variant="h1"
              customClass="mobile:text-center tablet:text-left"
            >
              {headerText}
            </Heading>
            {stepIndicator && (
              <span className="text-gray-500 text-base shrink-0">
                {stepIndicator}
              </span>
            )}
          </div>
          {headerSubtext && (
            <p className="text-sm text-gray-600 mt-2 mb-3 mobile:text-center tablet:text-left">
              {headerSubtext}
            </p>
          )}
          {text && (
            <p className="text-lg mt-1 mobile:text-center tablet:text-left">
              {text}
            </p>
          )}
          {children}
          {footer ? (
            <div className="mt-6">{footer}</div>
          ) : (
            <div className="flex mobile:flex-col-reverse tablet:flex-row justify-end mt-6 space-y-2 tablet:space-y-0 gap-x-2">
              <Button
                type="button"
                variant="reserve-outlined"
                onClick={() => {
                  onClose();
                  onCloseModal();
                }}
                data-testid="cancel-modal-button"
              >
                Cancel
              </Button>
              <Button
                type={formId ? 'submit' : 'button'}
                form={formId}
                onClick={formId ? undefined : onConfirm}
                variant="brand-filled"
                disabled={disabled}
                loading={loading || isSubmitting}
                data-testid="confirm-modal-button"
              >
                {onConfirmButtonLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
